import * as React from "react"
import { Redirect, Route } from "react-router-dom"
import { Action, LoadedAsyncState, loadingAsyncState } from "widgets-for-react"
import { previousOrDefault, transitionedToLoaded } from "../../../custom_api/api_utils"
import { AdviceFilter, AdviceSortMethod, AdviceType } from "../../../custom_api/bookarang/types"
import { fetchAdvice, fetchAdviceFilters } from "../../../custom_api/collection/api"
import { capitalizeFirstLetter } from "../../../custom_utils/helpers"
import { ApplicationState, BookarangState } from "../application"
import { AdviceState, initialHighlightState } from "./Advice/AdviceState"
import { AdviceWidget } from "./Advice/AdviceWidget"
import { BookBasedInspirationWidget } from "./BookBasedInspiration/BookBasedInspirationWidget"
import { MoodBasedInspirationWidget } from "./MoodBasedInspiration/MoodBasedInspirationWidget"
import { ThemeBasedInspirationWidget } from "./ThemeBasedInspiration/ThemeBasedInspirationWidget"
import { fetchCollectionItem } from "../../../../Shared/backendApi/CollectionApi"

type Updater = (_: Action<ApplicationState>) => void

const slug: { [key in AdviceType]: string } = {
  book: 'boek',
  theme: 'thema',
  mood: 'stemming'
}

export const promptUrl = (routePath: string, adviceType: AdviceType) => `/${routePath}/${slug[adviceType]}`

export const InspirationRoutes = (props: { state: BookarangState, setState: Updater }) => {
  const { state, setState } = props
  const { inspirationRoutePath: routePath, isKioskMode, branch, branches } = state
  const adviceProps = { routePath, isKioskMode, branch, branches }
  let highlightState = {}

  return [
    <Route exact key="inspiration" path={`/${routePath}/`} render={_ => { return <Redirect to={`/${routePath}/${slug.book}`} /> }} />,

    <Route exact key="inspiration/book" path={`/${routePath}/${slug.book}`} render={
      _ => {
        return BookBasedInspirationWidget({ ...state.inspirationState.bookBasedState, routePath, isKioskMode })
          .run(bookBasedState => setState((prevState) => ({ ...prevState, inspirationState: { ...prevState.inspirationState, bookBasedState } })))
      }
    } />,
    <Route exact key="inspiration/book/isbn" path={`/${routePath}/${slug.book}/:isbn*`} render={
      route => {
        const s0 = state.inspirationState.adviceState
        const selectedPrompt = route.match.params["isbn"] || s0.selectedPrompt
        const queryParams = new URLSearchParams(window.location.search)
        const title = queryParams.get("title")
        const author = queryParams.get("author")
        let { filters, sortMethod, advice, selectedBook } = s0

        if (selectedPrompt != s0.selectedPrompt) {
          selectedBook = loadingAsyncState(fetchCollectionItem(selectedPrompt));
          advice = loadingAsyncState(fetchAdvice('book', selectedPrompt + route.location.search), previousOrDefault(s0.advice, [])),
          filters = loadingAsyncState(fetchAdviceFilters(selectedPrompt), previousOrDefault(s0.filters, []))
          sortMethod = getSortMethod()
          highlightState = initialHighlightState
        }
        return AdviceWidget({ ...state.inspirationState.adviceState, selectedPrompt, adviceType: "book", filters, advice, sortMethod, title, author, ...highlightState, ...adviceProps, selectedBook })
          .run(adviceState => setState((prevState) => {
            if (filtersAreLoaded(adviceState, prevState)) {
              const queryParams: URLSearchParams = new URLSearchParams(window.location.search)
              adviceState.filters.value.forEach(f => {
                if (queryParams.get(`filter_${f.name}`)) {
                  f.metadata.value = Number(queryParams.get(`filter_${f.name}`))
                }
              });
            }
            return ({ ...prevState, inspirationState: { ...prevState.inspirationState, adviceState } })
        }))
      }
    } />,

    <Route exact key="inspiration/theme" path={`/${routePath}/${slug.theme}`} render={
      _ => {
        return ThemeBasedInspirationWidget({ ...state.inspirationState.themeBasedState, routePath, isKioskMode})
          .run(themeBasedState => setState((prevState) => ({ ...prevState, inspirationState: { ...prevState.inspirationState, themeBasedState } })))
      }
    } />,
    <Route exact key="inspiration/theme/theme" path={`/${routePath}/${slug.theme}/:theme`} render={

      route => {
        const s0 = state.inspirationState.adviceState
        const selectedPrompt = capitalizeFirstLetter(route.match.params["theme"]) || s0.selectedPrompt
        let { filters, sortMethod, advice } = s0

        if (selectedPrompt != s0.selectedPrompt) {
          advice = loadingAsyncState(fetchAdvice('theme', selectedPrompt + route.location.search), previousOrDefault(s0.advice, [])),
          filters = loadingAsyncState(fetchAdviceFilters(selectedPrompt), previousOrDefault(s0.filters, []))
          sortMethod = getSortMethod()
          highlightState = initialHighlightState
        }

        return AdviceWidget({ ...state.inspirationState.adviceState, selectedPrompt, adviceType: "theme", filters, advice, sortMethod, ...highlightState, ...adviceProps})
          .run(adviceState => setState((prevState) => {
            if (filtersAreLoaded(adviceState, prevState)) {
              const queryParams: URLSearchParams = new URLSearchParams(window.location.search)
              adviceState.filters.value.forEach(f => {
                if (queryParams.get(`filter_${f.name}`)) {
                  f.metadata.value = Number(queryParams.get(`filter_${f.name}`))
                }
              });
            }
            return ({ ...prevState, inspirationState: { ...prevState.inspirationState, adviceState } })
        }))
      }
    } />,

    <Route exact key="inspiration/mood" path={`/${routePath}/${slug.mood}`} render={
      _ => {
        return MoodBasedInspirationWidget({ ...state.inspirationState.moodBasedState, routePath, isKioskMode })
          .run(moodBasedState => setState((prevState) => ({ ...prevState, inspirationState: { ...prevState.inspirationState, moodBasedState } })))
      }
    } />,
    <Route key="inspiration/mood/mood" path={`/${routePath}/${slug.mood}/:mood`} render={
      route => {
        const s0 = state.inspirationState.adviceState
        let { filters, sortMethod, advice } = s0

        let selectedPrompt = route.match.params["mood"] || s0.selectedPrompt
        if (selectedPrompt != s0.selectedPrompt) {
          advice = loadingAsyncState(fetchAdvice('mood', selectedPrompt + route.location.search), previousOrDefault(s0.advice, [])),
          filters = loadingAsyncState(fetchAdviceFilters(selectedPrompt), previousOrDefault(s0.filters, []))
          sortMethod = getSortMethod()
          highlightState = initialHighlightState
        }

        return AdviceWidget({ ...state.inspirationState.adviceState, selectedPrompt, adviceType: "mood", filters, advice, sortMethod, ...highlightState, ...adviceProps })
          .run(adviceState => setState((prevState) => {
            if (filtersAreLoaded(adviceState, prevState)) {
              const queryParams: URLSearchParams = new URLSearchParams(window.location.search)
              adviceState.filters.value.forEach(f => {
                if (queryParams.get(`filter_${f.name}`)) {
                  f.metadata.value = Number(queryParams.get(`filter_${f.name}`))
                }
              });
            }
            return ({ ...prevState, inspirationState: { ...prevState.inspirationState, adviceState } })
        }))
      }
    } />
  ]
}

const getSortMethod = () => {
  const queryParams: URLSearchParams = new URLSearchParams(window.location.search)
  const sortValue: string | null = queryParams.get("sort")
  return sortValue ? sortValue as AdviceSortMethod : "match" as AdviceSortMethod
}

function filtersAreLoaded(adviceState: AdviceState, prevState: ApplicationState): adviceState is AdviceState & { filters: LoadedAsyncState<AdviceFilter[]> } {
  return transitionedToLoaded(prevState.inspirationState.adviceState.filters, adviceState.filters)
}